// src/Layout.jsx
import React from "react";
import { Outlet } from "react-router-dom";
import Header from "./components/ui/Header.jsx";
import { useAuth } from "./components/system/AppGate";
import OfflineBanner from "./components/offline/OfflineBanner";
import WeeklyExcelExportReminder from "./components/admin/WeeklyExcelExportReminder";
import { useOfflineStatus } from "./hooks/useOfflineStatus";

export const APP_THEME_STORAGE_KEY = "calins.app.theme";
export const DEFAULT_APP_THEME = "classic";
export const APP_THEME_OPTIONS = [
  { value: "classic", label: "Clasico" },
  { value: "violet", label: "Violeta" },
  { value: "copper", label: "Cobre" },
  { value: "midnight", label: "Azul noche" },
  { value: "navy", label: "Azul profundo" },
  { value: "ocean", label: "Océano" },
  { value: "forest", label: "Bosque" },
  { value: "bubblegum", label: "Rosado chicle" },
];

const APP_THEME_VALUES = new Set(APP_THEME_OPTIONS.map((option) => option.value));

export function isValidAppTheme(theme) {
  return APP_THEME_VALUES.has(theme);
}

export default function Layout() {
  const { role, loading } = useAuth();
  const offlineStatus = useOfflineStatus();
  const [appTheme, setAppTheme] = React.useState(DEFAULT_APP_THEME);

  React.useEffect(() => {
    try {
      const savedTheme = localStorage.getItem(APP_THEME_STORAGE_KEY);
      if (isValidAppTheme(savedTheme)) {
        setAppTheme(savedTheme);
      }
    } catch {
      setAppTheme(DEFAULT_APP_THEME);
    }
  }, []);

  React.useEffect(() => {
    document.documentElement.setAttribute("data-app-theme", appTheme);
    try {
      localStorage.setItem(APP_THEME_STORAGE_KEY, appTheme);
    } catch {
      // no-op
    }
  }, [appTheme]);

  const handleThemeChange = React.useCallback((nextTheme) => {
    if (!isValidAppTheme(nextTheme)) return;
    setAppTheme(nextTheme);
  }, []);

  // ⛔ Mientras se resuelve el rol, no renderizar nada
  if (loading) {
    return null;
  }

  // ⛔ Un técnico JAMÁS debe ver el layout comercial
  if (role === "technician") {
    return null;
  }

  return (
    <div className="min-h-screen text-foreground flex flex-col app-page-bg">
      <Header
        appTheme={appTheme}
        appThemeOptions={APP_THEME_OPTIONS}
        onAppThemeChange={handleThemeChange}
      />

      <div className="max-w-[1600px] mx-auto px-6 pt-4 w-full">
        <OfflineBanner status={offlineStatus} />
        <WeeklyExcelExportReminder role={role} />
      </div>

      {/* Contenido principal */}
      <main className="flex-1 max-w-[1600px] mx-auto px-6 py-4 w-full app-shell-bg rounded-xl">
        <Outlet />
      </main>

      {/* Footer con crédito y logo */}
      <footer className="mt-4 border-t border-white/10">
        <div className="max-w-[1600px] mx-auto px-6 py-3 text-[11px] text-slate-400 flex flex-col sm:flex-row items-center justify-between gap-2">
          <span className="opacity-60 text-center sm:text-left">
            Sistema de gestión de laboratorio de calibración
          </span>

          <a
            href="https://ideasdesign.website/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 hover:text-white transition-colors"
          >
            <img
              src="/ideas-logo.png"
              alt="Ideas Website"
              className="h-7 w-auto object-contain"
            />
            <span>
              Desarrollado por{" "}
              <span className="font-semibold text-sky-300">
                Ideas Website
              </span>
            </span>
          </a>
        </div>
      </footer>
    </div>
  );
}
